/**
 * Building blocks for the legal pages rendered inside `PublicLayout`
 * (cookies, refunds). They reproduce the look of privacy.tsx / terms.tsx —
 * numbered bold section heading, secondary-colour body, staggered FadeInDown —
 * so the pages read as one set, and add the pieces those two did not need:
 * a counsel-review banner, inline route links, bullet rows, and a storage
 * table that stays readable at 375pt.
 *
 * Colours come from `usePublicThemeColors`: PublicLayout is dark-only on the
 * web and theme-aware on native.
 */

import type { ReactNode } from 'react';
import { View, Text, Platform } from 'react-native';
import { useRouter, type Href } from 'expo-router';
import { AlertTriangle } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';

const MONO = Platform.select({ ios: 'Menlo', android: 'monospace', default: 'ui-monospace, Menlo, monospace' });

export function LegalSection({
  number,
  title,
  delay,
  children,
}: {
  number?: number;
  title: string;
  delay: number;
  children: ReactNode;
}) {
  const colors = usePublicThemeColors();
  return (
    <Animated.View entering={FadeInDown.delay(delay).duration(500)} style={{ marginBottom: 24 }}>
      <Text
        accessibilityRole="header"
        style={{ color: colors.text.primary }}
        className="text-base font-bold mb-2"
      >
        {number !== undefined ? `${number}. ${title}` : title}
      </Text>
      <View>{children}</View>
    </Animated.View>
  );
}

export function LegalSubheading({ children }: { children: ReactNode }) {
  const colors = usePublicThemeColors();
  return (
    <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mt-3 mb-1.5">
      {children}
    </Text>
  );
}

export function LegalParagraph({ children }: { children: ReactNode }) {
  const colors = usePublicThemeColors();
  return (
    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-2">
      {children}
    </Text>
  );
}

/** A bulleted line. The text column is flex-1 so it wraps instead of overflowing. */
export function LegalBullet({ children }: { children: ReactNode }) {
  const colors = usePublicThemeColors();
  return (
    <View className="flex-row mb-2">
      <Text style={{ color: colors.primary[500] }} className="text-sm leading-relaxed mr-2 shrink-0">
        {'•'}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed flex-1">
        {children}
      </Text>
    </View>
  );
}

/** Bold run inside a paragraph. */
export function Strong({ children }: { children: ReactNode }) {
  const colors = usePublicThemeColors();
  return (
    <Text style={{ color: colors.text.primary }} className="font-semibold">
      {children}
    </Text>
  );
}

/** Inline monospace run for a storage key or a path. */
export function Code({ children }: { children: ReactNode }) {
  const colors = usePublicThemeColors();
  return <Text style={{ color: colors.text.primary, fontFamily: MONO, fontSize: 13 }}>{children}</Text>;
}

/**
 * An in-app route link inside running text. Nested Text keeps it in the line;
 * the role makes it a link to assistive tech.
 */
export function LegalLink({ href, children }: { href: Href; children: ReactNode }) {
  const colors = usePublicThemeColors();
  const router = useRouter();
  return (
    <Text
      accessibilityRole="link"
      onPress={() => router.push(href)}
      style={{ color: colors.primary[500], textDecorationLine: 'underline' }}
      className="font-medium"
    >
      {children}
    </Text>
  );
}

/** Amber notice that the document is a draft awaiting counsel. */
export function CounselReviewBanner() {
  const colors = usePublicThemeColors();
  return (
    <Animated.View
      entering={FadeInDown.delay(80).duration(500)}
      accessibilityRole="alert"
      className="flex-row items-start gap-3 p-4 rounded-2xl mb-6 overflow-hidden"
      style={{ backgroundColor: colors.amber.light, borderWidth: 1, borderColor: colors.amber.muted }}
    >
      <View className="shrink-0 pt-0.5">
        <AlertTriangle size={18} color={colors.amber.main} />
      </View>
      <View className="flex-1">
        <Text numberOfLines={2} style={{ color: colors.text.primary }} className="text-xs font-bold tracking-wide">
          COUNSEL REVIEW REQUIRED
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
          This is a draft that has not yet been reviewed by legal counsel.
        </Text>
      </View>
    </Animated.View>
  );
}

export function LastUpdated({ date }: { date: string }) {
  const colors = usePublicThemeColors();
  return (
    <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-4">
      <Text numberOfLines={1} style={{ color: colors.text.tertiary }} className="text-sm">
        Last updated: {date}
      </Text>
    </Animated.View>
  );
}

export interface StorageRow {
  /** One or more storage keys, shown in monospace. */
  names: string[];
  where: string;
  purpose: string;
  party: 'First party' | 'Third party';
  duration: string;
}

/**
 * The cookie-policy table as a stack of cards: a four-column table cannot be
 * read at 375pt, a card per entry can. Every value wraps inside its own
 * column (flex-1 / flexShrink), and none is truncated — this is legal text,
 * so an ellipsis would hide part of what we disclose.
 */
export function StorageTable({ rows, label }: { rows: StorageRow[]; label: string }) {
  const colors = usePublicThemeColors();
  return (
    <View
      accessibilityLabel={label}
      className="rounded-2xl overflow-hidden mb-3"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
    >
      {rows.map((row, i) => (
        <View
          key={`storage-row-${i}`}
          className="px-4 py-3.5"
          style={i > 0 ? { borderTopWidth: 1, borderTopColor: colors.border.light } : undefined}
        >
          <View className="flex-row flex-wrap gap-x-2 gap-y-1 mb-1.5">
            {row.names.map((name, j) => (
              <Text
                key={`storage-name-${i}-${j}`}
                style={{ color: colors.text.primary, fontFamily: MONO, fontSize: 13, flexShrink: 1 }}
              >
                {name}
              </Text>
            ))}
          </View>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-2">
            {row.purpose}
          </Text>
          <MetaLine label="Where" value={row.where} />
          <MetaLine label="Party" value={row.party} />
          <MetaLine label="Duration" value={row.duration} />
        </View>
      ))}
    </View>
  );
}

function MetaLine({ label, value }: { label: string; value: string }) {
  const colors = usePublicThemeColors();
  return (
    <View className="flex-row mt-0.5">
      <Text numberOfLines={1} style={{ color: colors.text.tertiary, width: 72 }} className="text-xs font-semibold uppercase shrink-0">
        {label}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-xs leading-relaxed flex-1">
        {value}
      </Text>
    </View>
  );
}

/** Contact box matching the one at the end of privacy.tsx. */
export function LegalContactCard({ lines }: { lines: string[] }) {
  const colors = usePublicThemeColors();
  return (
    <View
      className="p-4 rounded-2xl mt-2 overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
    >
      {lines.map((line, i) => (
        <Text
          key={`contact-line-${i}`}
          style={{ color: i === 0 ? colors.text.primary : colors.text.secondary }}
          className={i === 0 ? 'font-semibold mb-1' : 'text-sm mb-1'}
        >
          {line}
        </Text>
      ))}
    </View>
  );
}
